import React, { useState, useEffect, useCallback } from 'react'
import Sidebar from './Sidebar'
import Header from './Header'

interface MainWrapperProps {
  children: React.ReactNode
}

const MainWrapper: React.FC<MainWrapperProps> = ({ children }) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024
      setIsMobile(mobile)

      // Auto-collapse on mobile
      if (mobile && !isSidebarCollapsed) {
        setIsSidebarCollapsed(true)
      }
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [isSidebarCollapsed])

  const handleSidebarToggle = useCallback(() => {
    setIsSidebarCollapsed(collapsed => !collapsed)
  }, [])

  // Handle keyboard navigation for accessibility
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'b') {
        event.preventDefault()
        handleSidebarToggle()
      }
      if (event.key === 'Escape' && isMobile && !isSidebarCollapsed) {
        handleSidebarToggle()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isMobile, isSidebarCollapsed, handleSidebarToggle])

  return (
    <div className="flex h-screen overflow-hidden bg-gray-70">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={handleSidebarToggle}
      />

      {isMobile && !isSidebarCollapsed && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={handleSidebarToggle}
          aria-label="Close sidebar"
        />
      )}

      <div
        className={`flex-1 flex flex-col transition-all duration-300 ease-in-out ${
          isSidebarCollapsed ? 'ml-16' : 'ml-64'
        } ${isMobile ? 'ml-0' : ''}`}
      >
        <Header />

        <main className="flex-1 overflow-auto px-6">
          <div className="min-w-md mx-auto">{children}</div>
        </main>
      </div>
    </div>
  )
}

export default MainWrapper
